import { c as _c } from "react/compiler-runtime";
import { Fragment, type ReactElement, type ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { removeBaseEndpoint } from "@/helpers/Helpers";
import ChatItemHrefButtons from "./ChatItemHrefButtons";
export interface OpenGraphPreviewData {
  title?: unknown | undefined;
  description?: unknown | undefined;
  url?: unknown | undefined;
  siteName?: unknown | undefined;
  site_name?: unknown | undefined;
  domain?: unknown | undefined;
  source?: unknown | undefined;
  canonicalUrl?: unknown | undefined;
  canonical_url?: unknown | undefined;
  image?: unknown | undefined;
  imageUrl?: unknown | undefined;
  image_url?: unknown | undefined;
  images?: unknown | undefined;
  ogImage?: unknown | undefined;
  og_image?: unknown | undefined;
  thumbnailUrl?: unknown | undefined;
  thumbnail_url?: unknown | undefined;
  [key: string]: unknown;
}
interface OpenGraphPreviewProps {
  readonly href: string;
  readonly preview?: OpenGraphPreviewData | null | undefined;
}
type MaybeRecord = Record<string, unknown>;
const TITLE_KEYS = ["title", "ogTitle", "name"];
const DESCRIPTION_KEYS = ["description", "ogDescription", "summary"];
const DOMAIN_KEYS = ["domain", "siteName", "site_name", "source"];
const URL_KEYS = ["url", "canonicalUrl", "canonical_url"];
const IMAGE_KEYS = ["image", "imageUrl", "image_url", "ogImage", "og_image", "thumbnailUrl", "thumbnail_url", "cover", "coverImage", "secureUrl"];
const IMAGE_COLLECTION_KEYS = ["images", "ogImages", "og_images", "thumbnails"];
const LONG_UNBROKEN_SEGMENT_THRESHOLD = 32;
function readFirstString(data: OpenGraphPreviewData | null | undefined, keys: readonly string[]): string | undefined {
  if (!data) {
    return undefined;
  }
  for (const key of keys) {
    const value = data[key];
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
    }
  }
  return undefined;
}
function extractImageFromValue(value: unknown): string | undefined {
  if (!value) {
    return undefined;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (Array.isArray(value)) {
    for (const entry of value) {
      const extracted = extractImageFromValue(entry);
      if (extracted) {
        return extracted;
      }
    }
    return undefined;
  }
  if (typeof value === "object") {
    const record = value as MaybeRecord;
    return extractImageFromValue(record["url"]) || extractImageFromValue(record["secure_url"]) || extractImageFromValue(record["secureUrl"]) || extractImageFromValue(record["src"]) || extractImageFromValue(record["href"]) || extractImageFromValue(record["image"]) || extractImageFromValue(record["thumbnail"]) || extractImageFromValue(record["contentUrl"]) || extractImageFromValue(record["content_url"]);
  }
  return undefined;
}
function extractImageUrl(data: OpenGraphPreviewData | null | undefined): string | undefined {
  if (!data) {
    return undefined;
  }
  for (const key of IMAGE_KEYS) {
    const imageUrl = extractImageFromValue(data[key]);
    if (imageUrl) {
      return imageUrl;
    }
  }
  for (const key of IMAGE_COLLECTION_KEYS) {
    const imageUrl = extractImageFromValue(data[key]);
    if (imageUrl) {
      return imageUrl;
    }
  }
  return undefined;
}
function wrapLongUnbrokenSegments(value: string | undefined): ReactNode {
  if (!value) {
    return value ?? "";
  }
  const tokens = value.split(/(\s+)/);
  let mutated = false;
  let offset = 0;
  const nodes = tokens.map(token => {
    if (!token) {
      return null;
    }
    const key = `og-wrap-${offset}`;
    offset += token.length;
    const trimmed = token.trim();
    if (trimmed.length >= LONG_UNBROKEN_SEGMENT_THRESHOLD) {
      mutated = true;
      return <span key={key} className="tw-break-all">
            {token}
          </span>;
    }
    return <Fragment key={key}>{token}</Fragment>;
  }).filter((token): token is ReactElement => token !== null);
  if (!mutated) {
    return value;
  }
  return nodes;
}
function extractDomainFromUrl(url: string | undefined): string | undefined {
  if (!url) {
    return undefined;
  }
  const trimmed = url.trim();
  if (!trimmed) {
    return undefined;
  }
  const candidates = [trimmed];
  if (!/^https?:\/\//i.test(trimmed)) {
    candidates.push(`https://${trimmed.replace(/^\/+/, "")}`);
    candidates.push(`http://${trimmed.replace(/^\/+/, "")}`);
  }
  for (const candidate of candidates) {
    try {
      const parsed = new URL(candidate);
      const hostname = parsed.hostname.replace(/^www\./i, "");
      if (hostname) {
        return hostname;
      }
    } catch {
      // ignore and continue
    }
  }
  return undefined;
}
function deriveDomain(href: string, preview: OpenGraphPreviewData | null | undefined): string | undefined {
  return readFirstString(preview, DOMAIN_KEYS) || extractDomainFromUrl(readFirstString(preview, URL_KEYS)) || extractDomainFromUrl(href);
}
function getRelativeHref(href: string): string | undefined {
  const relative = removeBaseEndpoint(href);
  if (typeof relative !== "string" || relative.length === 0) {
    return undefined;
  }
  return relative.startsWith("/") ? relative : undefined;
}
export function LinkPreviewCardLayout(t0) {
  const $ = _c(10);
  const {
    href,
    children
  } = t0;
  let t1;
  if ($[0] !== href) {
    t1 = getRelativeHref(href);
    $[0] = href;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const relativeHref = t1;
  let t2;
  if ($[2] !== children) {
    t2 = <div className="tw-flex-1 tw-min-w-0 tw-max-w-full tw-overflow-hidden focus-within:tw-overflow-visible">{children}</div>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== href || $[5] !== relativeHref) {
    t3 = <ChatItemHrefButtons href={href} relativeHref={relativeHref} />;
    $[4] = href;
    $[5] = relativeHref;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <div className="tw-flex tw-w-full tw-min-w-0 tw-max-w-full tw-items-stretch tw-gap-x-1">{t2}{t3}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export function hasOpenGraphContent(preview: OpenGraphPreviewData | null | undefined): boolean {
  if (!preview) {
    return false;
  }
  return Boolean(readFirstString(preview, TITLE_KEYS) || readFirstString(preview, DESCRIPTION_KEYS) || extractImageUrl(preview));
}
export default function OpenGraphPreview(t0) {
  const $ = _c(73);
  const {
    href,
    preview
  } = t0;
  let t1;
  if ($[0] !== href) {
    t1 = getRelativeHref(href);
    $[0] = href;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const relativeHref = t1;
  const effectiveHref = relativeHref ?? href;
  const isExternalLink = !relativeHref;
  const linkTarget = isExternalLink ? "_blank" : undefined;
  const linkRel = isExternalLink ? "noopener noreferrer" : undefined;
  if (typeof preview === "undefined") {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-4"><div className="tw-animate-pulse tw-flex tw-flex-col tw-gap-y-3" data-testid="og-preview-skeleton"><div className="tw-w-full tw-rounded-lg tw-bg-iron-800/60" /><div className="tw-h-4 tw-w-3/4 tw-rounded tw-bg-iron-800/40" /><div className="tw-h-3 tw-w-full tw-rounded tw-bg-iron-800/30" /><div className="tw-h-3 tw-w-2/3 tw-rounded tw-bg-iron-800/20" /></div></div>;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== href) {
      t3 = <LinkPreviewCardLayout href={href}>{t2}</LinkPreviewCardLayout>;
      $[3] = href;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  let t2;
  if ($[5] !== preview) {
    t2 = readFirstString(preview, TITLE_KEYS);
    $[5] = preview;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const title = t2;
  let T0;
  let t10;
  let t11;
  let t12;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[7] !== effectiveHref || $[8] !== href || $[9] !== linkRel || $[10] !== linkTarget || $[11] !== preview || $[12] !== title) {
    t12 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const description = readFirstString(preview, DESCRIPTION_KEYS);
      let t13;
      if ($[24] !== preview) {
        t13 = extractImageUrl(preview);
        $[24] = preview;
        $[25] = t13;
      } else {
        t13 = $[25];
      }
      const imageUrl = t13;
      let t14;
      if ($[26] !== href || $[27] !== preview) {
        t14 = deriveDomain(href, preview);
        $[26] = href;
        $[27] = preview;
        $[28] = t14;
      } else {
        t14 = $[28];
      }
      const domain = t14;
      const hasContent = Boolean(title || description || imageUrl);
      if (!hasContent) {
        let t15;
        if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
          t15 = <p className="tw-m-0 tw-text-sm tw-font-medium tw-text-iron-400">Link unavailable</p>;
          $[29] = t15;
        } else {
          t15 = $[29];
        }
        const t16 = domain ?? href;
        let t17;
        if ($[30] !== t16) {
          t17 = wrapLongUnbrokenSegments(t16);
          $[30] = t16;
          $[31] = t17;
        } else {
          t17 = $[31];
        }
        let t18;
        if ($[32] !== effectiveHref || $[33] !== linkRel || $[34] !== linkTarget || $[35] !== t17) {
          t18 = <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-6" data-testid="og-preview-unavailable"><div className="tw-text-center tw-space-y-2">{t15}<Link href={effectiveHref} target={linkTarget} rel={linkRel} className="tw-break-words tw-[overflow-wrap:anywhere] tw-text-sm tw-font-semibold tw-text-iron-100 tw-no-underline tw-transition tw-duration-200 hover:tw-text-white">{t17}</Link></div></div>;
          $[32] = effectiveHref;
          $[33] = linkRel;
          $[34] = linkTarget;
          $[35] = t17;
          $[36] = t18;
        } else {
          t18 = $[36];
        }
        let t19;
        if ($[37] !== href || $[38] !== t18) {
          t19 = <LinkPreviewCardLayout href={href}>{t18}</LinkPreviewCardLayout>;
          $[37] = href;
          $[38] = t18;
          $[39] = t19;
        } else {
          t19 = $[39];
        }
        t12 = t19;
        break bb0;
      }
      T0 = LinkPreviewCardLayout;
      t11 = href;
      t9 = "tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-4";
      t10 = "og-preview-card";
      t7 = "tw-flex tw-flex-col tw-gap-4 md:tw-flex-row";
      if ($[40] !== domain || $[41] !== effectiveHref || $[42] !== imageUrl || $[43] !== linkRel || $[44] !== linkTarget || $[45] !== title) {
        t8 = imageUrl && <Link href={effectiveHref} target={linkTarget} rel={linkRel} className="tw-block md:tw-w-60 md:tw-flex-shrink-0"><div className="tw-overflow-hidden tw-rounded-lg tw-bg-iron-900/60"><Image src={imageUrl} alt={title ?? domain ?? "Link preview"} width={1200} height={630} className="tw-h-full tw-w-full tw-object-cover" loading="lazy" sizes="(max-width: 768px) 100vw, 240px" unoptimized={true} /></div></Link>;
        $[40] = domain;
        $[41] = effectiveHref;
        $[42] = imageUrl;
        $[43] = linkRel;
        $[44] = linkTarget;
        $[45] = title;
        $[46] = t8;
      } else {
        t8 = $[46];
      }
      t3 = "tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-y-2";
      if ($[47] !== domain) {
        t4 = domain && <span className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-iron-400">{wrapLongUnbrokenSegments(domain)}</span>;
        $[47] = domain;
        $[48] = t4;
      } else {
        t4 = $[48];
      }
      const t15 = title ?? domain ?? href;
      let t16;
      if ($[49] !== t15) {
        t16 = wrapLongUnbrokenSegments(t15);
        $[49] = t15;
        $[50] = t16;
      } else {
        t16 = $[50];
      }
      if ($[51] !== effectiveHref || $[52] !== linkRel || $[53] !== linkTarget || $[54] !== t16) {
        t5 = <Link href={effectiveHref} target={linkTarget} rel={linkRel} className="tw-break-words tw-[overflow-wrap:anywhere] tw-text-lg tw-font-semibold tw-leading-snug tw-text-iron-100 tw-no-underline tw-transition tw-duration-200 hover:tw-text-white">{t16}</Link>;
        $[51] = effectiveHref;
        $[52] = linkRel;
        $[53] = linkTarget;
        $[54] = t16;
        $[55] = t5;
      } else {
        t5 = $[55];
      }
      t6 = description && <p className="tw-m-0 tw-text-sm tw-text-iron-300 tw-line-clamp-3 tw-break-words tw-[overflow-wrap:anywhere] tw-whitespace-pre-line">{wrapLongUnbrokenSegments(description)}</p>;
    }
    $[7] = effectiveHref;
    $[8] = href;
    $[9] = linkRel;
    $[10] = linkTarget;
    $[11] = preview;
    $[12] = title;
    $[13] = T0;
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    T0 = $[13];
    t10 = $[14];
    t11 = $[15];
    t12 = $[16];
    t3 = $[17];
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
    t7 = $[21];
    t8 = $[22];
    t9 = $[23];
  }
  if (t12 !== Symbol.for("react.early_return_sentinel")) {
    return t12;
  }
  let t13;
  if ($[56] !== t3 || $[57] !== t4 || $[58] !== t5 || $[59] !== t6) {
    t13 = <div className={t3}>{t4}{t5}{t6}</div>;
    $[56] = t3;
    $[57] = t4;
    $[58] = t5;
    $[59] = t6;
    $[60] = t13;
  } else {
    t13 = $[60];
  }
  let t14;
  if ($[61] !== t13 || $[62] !== t7 || $[63] !== t8) {
    t14 = <div className={t7}>{t8}{t13}</div>;
    $[61] = t13;
    $[62] = t7;
    $[63] = t8;
    $[64] = t14;
  } else {
    t14 = $[64];
  }
  let t15;
  if ($[65] !== t10 || $[66] !== t14 || $[67] !== t9) {
    t15 = <div className={t9} data-testid={t10}>{t14}</div>;
    $[65] = t10;
    $[66] = t14;
    $[67] = t9;
    $[68] = t15;
  } else {
    t15 = $[68];
  }
  let t16;
  if ($[69] !== T0 || $[70] !== t11 || $[71] !== t15) {
    t16 = <T0 href={t11}>{t15}</T0>;
    $[69] = T0;
    $[70] = t11;
    $[71] = t15;
    $[72] = t16;
  } else {
    t16 = $[72];
  }
  return t16;
}